import styled, { css } from "styled-components";
import React, { CSSProperties } from "react";
import {
  useIconWithFallback,
  useIconWithFallbackProps,
} from "Global/useIconWithFallback.tsx";
import Tooltip from "common/Tooltips/Tooltip";

export interface IconWithFallbackProps extends useIconWithFallbackProps {
  iconStyle?: CSSProperties;
  iconSize?: IconSize;
  pxIconSize?: number;
  withTooltip?: boolean;
}

const IconWithFallback = ({
  coinOrExchangeName,
  iconStyle,
  iconSize = "m",
  pxIconSize,
  withTooltip = false,
  ...useIconWithFallbackProps
}: IconWithFallbackProps) => {
  const iconData = useIconWithFallback({
    ...useIconWithFallbackProps,
    coinOrExchangeName,
  });
  const { error, fallbackLetter, handleError, iconUrl, imageFilters } =
    iconData;
  if (!("iconUrl" in iconData)) return null;

  let icon = error ? (
    <FallbackIcon style={iconStyle} $size={iconSize} $pxIconSize={pxIconSize}>
      {fallbackLetter}
    </FallbackIcon>
  ) : (
    <IconImage
      style={{ ...iconStyle, ...imageFilters }}
      $size={iconSize}
      $pxIconSize={pxIconSize}
      src={iconUrl}
      onError={handleError}
    />
  );

  if (withTooltip) {
    icon = <Tooltip title={coinOrExchangeName}>{icon}</Tooltip>;
  }

  return icon;
};

export default React.memo(IconWithFallback);

const SIZE_TO_ICON_SIZE_MAPPING = {
  xs: 0.6,
  s: 0.8,
  m: 1,
  l: 1.2,
  xl: 1.4,
  xxl: 1.6,
  xxxl: 2,
};
export type IconSize = keyof typeof SIZE_TO_ICON_SIZE_MAPPING;

const getIconSize = (size: IconSize, pxIconSize?: number) => {
  let sizeString = pxIconSize
    ? `${pxIconSize}px`
    : `${SIZE_TO_ICON_SIZE_MAPPING[size]}em`;
  return css`
    width: ${sizeString};
    height: ${sizeString};
  `;
};
const FallbackIcon = styled.div<{ $size: IconSize; $pxIconSize?: number }>`
  background: ${(p) => p.theme.colors.primaryBlue};
  border-radius: 50%;
  color: ${(p) => p.theme.colors.buttonText};
  display: flex;
  justify-content: center;
  align-items: center;
  flex: none;
  ${(p) => getIconSize(p.$size, p.$pxIconSize)};
`;

const IconImage = styled.img<{ $size: IconSize; $pxIconSize?: number }>`
  ${(p) => getIconSize(p.$size, p.$pxIconSize)};
`;
